/**
 * Convert displayed dimensions from metric (cm, kg) to US customary for NA shoppers.
 * Gated by shipping country (US / CA) from Liquid via window.NOOMA_COUNTRY — not market handle.
 *
 * Covers PDP: dimensions accordions, variant picker labels, sticky bar, cart drawer line items,
 * upsell, shop the look, pairs well with, predictive search variant lines.
 * Size chart cm/inch toggle is handled in dev-size-chart-drawer.js.
 */

/** @type {readonly string[]} */
const IMPERIAL_COUNTRY_CODES = ['US', 'CA'];

/** @returns {boolean} */
function shouldConvert() {
  const code = window.NOOMA_COUNTRY;
  if (typeof code !== 'string' || !code.length) {
    return false;
  }

  return IMPERIAL_COUNTRY_CODES.includes(code);
}

/**
 * @param {string} str
 * @returns {number}
 */
function parseFirstMetricNumber(str) {
  const match = String(str).match(/-?[\d.,]+/);
  if (!match) {
    return Number.NaN;
  }

  let normalized = match[0];
  if (normalized.includes('.') && normalized.includes(',')) {
    if (normalized.lastIndexOf('.') > normalized.lastIndexOf(',')) {
      normalized = normalized.replace(/,/g, '');
    } else {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    }
  } else {
    normalized = normalized.replace(/,/g, '.');
  }

  return Number.parseFloat(normalized);
}

/**
 * @param {number} n
 * @returns {number}
 */
function cmToInchDisplay(n) {
  return Math.round(n * 0.3937 * 10) / 10;
}

/**
 * @param {string} text
 * @returns {string}
 */
function convertTextWithDimensions(text) {
  if (!text.includes('cm') && !text.includes('kg')) {
    return text;
  }

  let output = text;

  output = output.replace(
    /(-?[\d.,]+)\s*[x×]\s*(-?[\d.,]+)\s*cm/gi,
    (match, first, second) => {
      const firstValue = parseFirstMetricNumber(first);
      const secondValue = parseFirstMetricNumber(second);
      if (Number.isNaN(firstValue) || Number.isNaN(secondValue)) {
        return match;
      }

      return `${cmToInchDisplay(firstValue)} x ${cmToInchDisplay(secondValue)} (in)`;
    }
  );

  if (output.includes('cm')) {
    output = output.replace(/(-?[\d.,]+)\s*cm\b/gi, (match, num) => {
      const value = parseFirstMetricNumber(num);
      if (Number.isNaN(value)) {
        return match;
      }

      return `${cmToInchDisplay(value)} (in)`;
    });
  }

  if (output.includes('kg')) {
    output = output.replace(/(-?[\d.,]+)\s*kg\b/gi, (match, num) => {
      const value = parseFirstMetricNumber(num);
      if (Number.isNaN(value)) {
        return match;
      }

      return `${Math.round(value * 2.2046 * 10) / 10} lbs`;
    });
  }

  return output;
}

/**
 * @param {Node} root
 * @param {(text: string) => string} transform
 */
function applyTextTransformToTextNodes(root, transform) {
  if (!(root instanceof Element)) {
    return;
  }

  /** @param {Element} element */
  const walk = (element) => {
    for (const node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text && (text.includes('cm') || text.includes('kg'))) {
          const next = transform(text);
          if (next !== text) {
            node.textContent = next;
          }
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const elementNode = node;
        if (elementNode.classList?.contains('swatch')) {
          continue;
        }

        walk(elementNode);
      }
    }
  };

  walk(root);
}

/**
 * @param {Element} element
 * @param {(text: string) => string} transform
 */
function convertElementText(element, transform) {
  if (element.childNodes.length) {
    applyTextTransformToTextNodes(element, transform);
    return;
  }

  const text = element.textContent;
  if (text && (text.includes('cm') || text.includes('kg'))) {
    const next = transform(text);
    if (next !== text) {
      element.textContent = next;
    }
  }
}

/** @param {ParentNode} [scope] */
function convertMerchandisingBlocks(scope = document) {
  if (!shouldConvert()) {
    return;
  }

  const selector = [
    '.cart-items__variant',
    '.sticky-add-to-cart__variant',
    '.cart-drawer-upsell-card__option',
    '.dev-pairs-well-with__variant-title',
    '.dev-shop-the-look__product-row-subtitle',
    '.predictive-search .resource-card__subheading',
    '.predictive-search .predictive-search-results__card .variant-option__label',
  ].join(', ');

  for (const element of scope.querySelectorAll(selector)) {
    convertElementText(element, convertTextWithDimensions);
  }
}

/** @param {ParentNode} [scope] */
function convertVariantOptionLabels(scope = document) {
  if (!shouldConvert()) {
    return;
  }

  for (const picker of scope.querySelectorAll('variant-picker')) {
    const targets = picker.querySelectorAll(
      'label[data-option-value], [data-option-value-id], button[role="option"], [id$="-selected-value"], .variant-option__button-label__text'
    );

    for (const element of targets) {
      const tooltip = element.getAttribute('data-tooltip');
      if (tooltip && (tooltip.includes('cm') || tooltip.includes('kg'))) {
        const next = convertTextWithDimensions(tooltip);
        if (next !== tooltip) {
          element.setAttribute('data-tooltip', next);
        }
      }

      if (element.getAttribute('role') === 'option' && element.getAttribute('title')) {
        const title = element.getAttribute('title');
        if (title && (title.includes('cm') || title.includes('kg'))) {
          const next = convertTextWithDimensions(title);
          if (next !== title) {
            element.setAttribute('title', next);
          }
        }
      }

      convertElementText(element, convertTextWithDimensions);
    }
  }
}

/** @param {ParentNode} [scope] */
function convertDynamicProductInfoBlocks(scope = document) {
  if (!shouldConvert()) {
    return;
  }

  const selector = [
    'dev-dimensions-toggler [data-name="dimensions-element"]',
    'dev-dimensions-toggler [data-name="materials-element"]',
    'dev-dimensions-toggler [data-name="finish-element"]',
    'dev-dimensions-toggler [data-name="relevant-element"]',
    'dev-dimensions-toggler [data-name="width-id"]',
    'dev-dimensions-toggler [data-name="width-mattress-id"]',
    'dev-dimensions-toggler .dev-dim__dims-val',
    'dev-dimensions-toggler .dev-dim__list-item',
  ].join(', ');

  for (const element of scope.querySelectorAll(selector)) {
    convertElementText(element, convertTextWithDimensions);
  }
}

/** @param {ParentNode} [scope] */
function convertMetricToImperial(scope = document) {
  if (!shouldConvert()) {
    return;
  }

  convertDynamicProductInfoBlocks(scope);
  convertVariantOptionLabels(scope);
  convertMerchandisingBlocks(scope);
}

/** @param {ParentNode} [scope] */
function scheduleConvert(scope = document) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      convertMetricToImperial(scope);
    });
  });
}

/**
 * @param {Node} node
 * @returns {boolean}
 */
function mutationHasRelevantContent(node) {
  if (!(node instanceof Element)) {
    return false;
  }

  if (node.matches('variant-picker, dev-dimensions-toggler, predictive-search, dev-pairs-well-with, dev-shop-the-look')) {
    return true;
  }

  if (node.classList?.contains('cart-drawer-upsell') || node.classList?.contains('cart-items__table-row')) {
    return true;
  }

  if (node.querySelector?.('variant-picker, dev-dimensions-toggler, .cart-drawer-upsell')) {
    return true;
  }

  return false;
}

if (shouldConvert()) {
  document.addEventListener('DOMContentLoaded', () => {
    convertMetricToImperial();
  });

  document.addEventListener('variant:update', () => {
    scheduleConvert();
  });

  document.addEventListener('cart:update', () => {
    requestAnimationFrame(() => {
      convertMerchandisingBlocks();
    });
  });

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      if (event.target.closest('.dev-shop-the-look__hotspot-trigger, .dev-shop-the-look__hotspot')) {
        setTimeout(() => {
          convertMerchandisingBlocks();
        }, 0);
      }
    },
    true
  );

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (mutationHasRelevantContent(node)) {
          convertMetricToImperial();
          return;
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });

  convertMetricToImperial();
}
